import { v4 as uuidv4 } from 'uuid';
import { mapFieldDesc } from '90.quickConnect.Models/mappings';
import { ConsentFrequency, FieldType } from '90.quickConnect.Models/enums';
import { DeclarationContext, FieldDesc, RepeatableGroupDesc } from '90.quickConnect.Models/models';

// Garde-fou global: aucune commande QCScript ne peut depasser 80 lignes
export const REPEATABLE_GROUP_MAX_ROWS = 80;

export enum RepeatableGroupAction {
  RemoveAllRows = 'RemoveAllRows',
  SetRowCount = 'SetRowCount',
  RemoveRowAt = 'RemoveRowAt',
  AppendRow = 'AppendRow',
}

export type RepeatableGroupCommand =
  | { type: RepeatableGroupAction.AppendRow; value: number }
  | { type: RepeatableGroupAction.RemoveRowAt; value: number }
  | { type: RepeatableGroupAction.SetRowCount; value: number }
  | { type: RepeatableGroupAction.RemoveAllRows };

export const parseRepeatableGroupCommand = (value: string): RepeatableGroupCommand | null => {
  // Les commandes QCScript arrivent sous forme texte via setValue("RG", "Action:param").
  const normalizedValue = value.trim();
  const [rawAction, rawArg] = normalizedValue.split(':', 2);
  const action = rawAction.trim().toLowerCase();
  const arg = rawArg?.trim();

  if (action === RepeatableGroupAction.RemoveAllRows.toLowerCase()) {
    return { type: RepeatableGroupAction.RemoveAllRows };
  }

  if (arg === undefined || !/^\d+$/.test(arg)) {
    return null;
  }

  const numericArg = Number(arg);

  // Un argument superieur au cap global est invalide: setValue retournera false.
  if (numericArg > REPEATABLE_GROUP_MAX_ROWS) {
    return null;
  }

  if (action === RepeatableGroupAction.AppendRow.toLowerCase()) {
    return { type: RepeatableGroupAction.AppendRow, value: numericArg };
  }

  if (action === RepeatableGroupAction.RemoveRowAt.toLowerCase()) {
    return { type: RepeatableGroupAction.RemoveRowAt, value: numericArg };
  }

  if (action === RepeatableGroupAction.SetRowCount.toLowerCase()) {
    return { type: RepeatableGroupAction.SetRowCount, value: numericArg };
  }

  return null;
};

const createIncludeRow = (
  repeatableGroup: RepeatableGroupDesc,
  context: DeclarationContext,
  rowIndex: number,
): FieldDesc => {
  // Reproduit exactement la structure d'une ligne ajoutee
  const { id, fullPathId, groupTemplate } = repeatableGroup;
  const { templateBodies } = context;

  const safeFullPathId = fullPathId || id;
  const parentIdForChildren = `${safeFullPathId}.${id}.${rowIndex}`;

  const newIncludeChildren = groupTemplate.map((templateField) =>
    mapFieldDesc(templateField, parentIdForChildren, templateBodies, context),
  );

  return {
    stateId: uuidv4(),
    fieldType: FieldType.Include,
    items: newIncludeChildren,
    value: true,
    errors: undefined,
    fullPathId: parentIdForChildren,
    id: `${id}.${rowIndex}`,
    fieldIsReadOnly: false,
    isVisible: true,
    checkRGPD: ConsentFrequency.Undef,
  };
};

const reindexDescendants = (fields: FieldDesc[], oldPrefix: string, newPrefix: string): FieldDesc[] =>
  // Tous les descendants d'une ligne ont un fullPathId qui commence par celui de la ligne (ex: RG.RG.1.texte, RG.RG.1.sousGroupe.x)
  fields.map((field) => ({
    ...field,
    fullPathId: field.fullPathId.startsWith(oldPrefix)
      ? `${newPrefix}${field.fullPathId.slice(oldPrefix.length)}`
      : field.fullPathId,
    items: field.items ? reindexDescendants(field.items, oldPrefix, newPrefix) : field.items,
  }));

const reindexItems = (repeatableGroup: RepeatableGroupDesc, items: FieldDesc[]): FieldDesc[] => {
  // Apres suppression/troncature, on renumerote les lignes et tous leurs descendants,
  // sinon une ligne ajoutee ensuite reutiliserait des fullPathId encore portes par une ligne existante.
  return items.map((item, newIndex) => {
    const oldId = item.id;
    const oldFullPathId = item.fullPathId;

    const newId = `${repeatableGroup.id}.${newIndex}`;
    const newFullPathId = oldFullPathId.endsWith(oldId)
      ? `${oldFullPathId.slice(0, oldFullPathId.length - oldId.length)}${newId}`
      : oldFullPathId;

    return {
      ...item,
      id: newId,
      fullPathId: newFullPathId,
      items:
        newFullPathId === oldFullPathId
          ? item.items
          : reindexDescendants(item.items, `${oldFullPathId}.`, `${newFullPathId}.`),
    };
  });
};

export const appendRowsToRepeatableGroup = (
  repeatableGroup: RepeatableGroupDesc,
  context: DeclarationContext,
  rowsToAdd: number,
): { nextItems: FieldDesc[]; addedRows: FieldDesc[]; addedCount: number } => {
  if (rowsToAdd <= 0) {
    return { nextItems: repeatableGroup.items, addedRows: [], addedCount: 0 };
  }

  const currentItems = [...repeatableGroup.items];
  const addedRows: FieldDesc[] = [];

  // La limite reste celle du formulaire (maxRow).
  const effectiveMax = repeatableGroup.maxRow;

  while (currentItems.length < effectiveMax && addedRows.length < rowsToAdd) {
    const includeRow = createIncludeRow(repeatableGroup, context, currentItems.length);
    currentItems.push(includeRow);
    addedRows.push(includeRow);
  }

  return {
    nextItems: currentItems,
    addedRows,
    addedCount: addedRows.length,
  };
};

export const removeRowAtFromRepeatableGroup = (
  repeatableGroup: RepeatableGroupDesc,
  rowIndex: number,
): { nextItems: FieldDesc[]; removed: boolean } => {
  if (rowIndex < 0 || rowIndex >= repeatableGroup.items.length) {
    return { nextItems: repeatableGroup.items, removed: false };
  }

  const nextItems = [...repeatableGroup.items];
  nextItems.splice(rowIndex, 1);

  return {
    nextItems: reindexItems(repeatableGroup, nextItems),
    removed: true,
  };
};

export const setRowCountForRepeatableGroup = (
  repeatableGroup: RepeatableGroupDesc,
  context: DeclarationContext,
  rowCount: number,
): { nextItems: FieldDesc[]; changed: boolean } => {
  // SetRowCount pilote a la fois l'ajout, la suppression partielle et RemoveAllRows.
  // La limite reste celle du formulaire (maxRow).
  const effectiveMax = repeatableGroup.maxRow;
  const normalizedRowCount = Math.max(0, Math.min(rowCount, effectiveMax));
  const currentCount = repeatableGroup.items.length;

  if (normalizedRowCount === currentCount) {
    return { nextItems: repeatableGroup.items, changed: false };
  }

  if (normalizedRowCount > currentCount) {
    const { nextItems } = appendRowsToRepeatableGroup(repeatableGroup, context, normalizedRowCount - currentCount);
    return { nextItems, changed: true };
  }

  const trimmedItems = repeatableGroup.items.slice(0, normalizedRowCount);
  return {
    nextItems: reindexItems(repeatableGroup, trimmedItems),
    changed: true,
  };
};

export const updateRepeatableGroupByCommand = (
  repeatableGroup: RepeatableGroupDesc,
  context: DeclarationContext,
  commandAsString: string,
): { nextItems: FieldDesc[]; changed: boolean } => {
  // Point d'entree unique pour traduire une commande QCScript en mutation structurelle.
  const parsedCommand = parseRepeatableGroupCommand(commandAsString);

  if (!parsedCommand) {
    return { nextItems: repeatableGroup.items, changed: false };
  }

  if (parsedCommand.type === RepeatableGroupAction.AppendRow) {
    const { nextItems, addedCount } = appendRowsToRepeatableGroup(repeatableGroup, context, parsedCommand.value);
    return { nextItems, changed: addedCount > 0 };
  }

  if (parsedCommand.type === RepeatableGroupAction.SetRowCount) {
    return setRowCountForRepeatableGroup(repeatableGroup, context, parsedCommand.value);
  }

  if (parsedCommand.type === RepeatableGroupAction.RemoveAllRows) {
    return setRowCountForRepeatableGroup(repeatableGroup, context, 0);
  }

  // RemoveRowAt est interprete en 0-based: 0 = 1ere ligne.
  const removeResult = removeRowAtFromRepeatableGroup(repeatableGroup, parsedCommand.value);

  return { nextItems: removeResult.nextItems, changed: removeResult.removed };
};
